// The scope chooser (U7), one component for dismiss, restore from Set aside and a verdict: just
// this once · this sender · this domain · this sender and subject. Keys: 1–4 choose a scope,
// c / b / r / s pick where that mail goes, Esc (or 1) keeps it to this once. A wider scope files
// a proposal on Learning (invariant 4): nothing changes until the owner applies it there.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import { DESTINATIONS, type Destination } from "@shared/routing";
import type { TeachOption, TeachScope } from "@shared/teach";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { teachApi, teachKeys } from "@web/lib/teachApi";
import { registerUndo } from "@web/lib/undoRegistry";
import { Kbd } from "@web/ui/Kbd";
import { Modal } from "@web/ui/Modal";
import {
  canChoose,
  filedMessage,
  handlingForKey,
  handlingWords,
  HANDLING_KEY,
  initialHandling,
  scopeForKey,
  SURFACE_HEADING,
  teachRequest,
  type TeachTarget,
} from "./teach";
import { teachStore, useTeachState } from "./teachStore";

/** Mounted once in the shell; opens over any screen. */
export function TeachHost() {
  const state = useTeachState();
  return (
    <Modal open={state.open} onClose={teachStore.close} label="Just this once, or every time?">
      {state.open && state.target ? (
        <TeachChooser key={state.seq} target={state.target} onClose={teachStore.close} />
      ) : null}
    </Modal>
  );
}

function failureWords(error: unknown): string {
  if (isApiError(error, "conflict")) return "That rule is already in place. Nothing changed.";
  if (isApiError(error, "invalid_request") && error.serverMessage) return `${error.serverMessage}.`;
  return "Couldn’t file that just now. Nothing changed.";
}

function ScopeRow({
  option,
  index,
  busy,
  onPick,
}: {
  option: TeachOption;
  index: number;
  busy: boolean;
  onPick: () => void;
}) {
  const ok = canChoose(option);
  return (
    <button
      type="button"
      disabled={!ok || busy}
      onClick={onPick}
      className={cn(
        "flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left",
        index === 0 ? "border-accent/50 bg-accent-soft" : "border-line bg-bg hover:bg-sunk/60",
        !ok && "cursor-not-allowed opacity-60 hover:bg-bg",
      )}
    >
      <span className="min-w-0">
        <span className="block text-ink">{option.label}</span>
        <span className="block truncate text-sm text-muted">
          {option.unavailable ?? option.detail}
        </span>
      </span>
      <Kbd className="hidden shrink-0 sm:inline-flex">{String(index + 1)}</Kbd>
    </button>
  );
}

function HandlingPicker({
  value,
  onChange,
}: {
  value: Destination;
  onChange: (d: Destination) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Where that mail goes" className="flex flex-wrap gap-1.5">
      {DESTINATIONS.map((d) => (
        <button
          key={d}
          type="button"
          role="radio"
          aria-checked={value === d}
          onClick={() => onChange(d)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm",
            value === d ? "border-accent/50 bg-accent-soft text-ink" : "border-line text-ink-2",
          )}
        >
          {handlingWords(d)}
          <Kbd className="hidden sm:inline-flex">{HANDLING_KEY[d].toUpperCase()}</Kbd>
        </button>
      ))}
    </div>
  );
}

function useFile(target: TeachTarget, onClose: () => void) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const file = async (scope: TeachScope, handling: Destination) => {
    if (busy) return;
    if (scope === "once") return onClose();
    setBusy(true);
    try {
      const proposal = await teachApi.file(teachRequest(target, scope, handling));
      void queryClient.invalidateQueries({ queryKey: ["proposals"] });
      registerUndo({
        message: filedMessage(proposal.label),
        undo: async () => {
          await feedApi.rejectProposal(proposal.id);
          void queryClient.invalidateQueries({ queryKey: ["proposals"] });
        },
        doneMessage: "Withdrawn. Just this once, then.",
      });
      onClose();
    } catch (error) {
      toast(failureWords(error));
    } finally {
      setBusy(false);
    }
  };
  return { file, busy };
}

function TeachChooser({ target, onClose }: { target: TeachTarget; onClose: () => void }) {
  const options = useQuery({
    queryKey: teachKeys.options(target.threadKey),
    queryFn: ({ signal }) => teachApi.options(target.threadKey, signal),
    staleTime: 60_000,
  });
  const [handling, setHandling] = useState<Destination>(() => initialHandling(target));
  const { file, busy } = useFile(target, onClose);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => rootRef.current?.focus({ preventScroll: true }), []);
  const list = options.data?.options ?? [];

  const pick = (scope: TeachScope) => {
    const option = list.find((o) => o.scope === scope);
    if (scope === "once" || canChoose(option)) void file(scope, handling);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    const scope = scopeForKey(e.key);
    if (scope) {
      e.preventDefault();
      pick(scope);
      return;
    }
    const d = handlingForKey(e.key);
    if (d) {
      e.preventDefault();
      setHandling(d);
    }
  };

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex max-h-[85dvh] flex-col outline-none"
      data-testid="teach-chooser"
    >
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <p className="mm-kicker">{SURFACE_HEADING[target.surface]}</p>
          <h2 className="mt-1 text-xl leading-snug break-words">{target.title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Just this once"
          className="rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm text-muted">Every time, their mail goes to</span>
          <HandlingPicker value={handling} onChange={setHandling} />
        </div>
        {options.isError ? (
          <p className="text-sm text-muted">
            Couldn’t look up this sender just now. Esc keeps it to this once.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {(list.length ? list : PLACEHOLDER).map((o, i) => (
              <ScopeRow
                key={o.scope}
                option={o}
                index={i}
                busy={busy || options.isPending}
                onPick={() => pick(o.scope)}
              />
            ))}
          </div>
        )}
        <span className="text-xs text-muted">
          <Kbd>1</Kbd>–<Kbd>4</Kbd> choose · <Kbd>C</Kbd> <Kbd>B</Kbd> <Kbd>R</Kbd> <Kbd>S</Kbd>{" "}
          where it goes · <Kbd>Esc</Kbd> just this once
        </span>
        <p className="text-xs text-muted">
          A wider choice becomes a proposal on Learning, with the last 30 days it would have
          touched. Nothing changes until you apply it, and Gmail is never touched.
        </p>
      </div>
    </div>
  );
}

const PLACEHOLDER: TeachOption[] = [
  { scope: "once", label: "Just this once", detail: "Nothing else changes", unavailable: null },
  { scope: "sender", label: "This sender", detail: "…", unavailable: null },
  { scope: "domain", label: "This domain", detail: "…", unavailable: null },
  { scope: "sender_subject", label: "This sender and subject", detail: "…", unavailable: null },
];
